import React, { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import { alpha } from '@mui/material/styles';

import axiosInstance from '../utils/axiosInstance';

const mediaUrlCache = new Map();
const CACHE_EXPIRY_SAFETY_RATIO = 0.1;
const MAX_CACHE_EXPIRY_SAFETY_MS = 30000;

function getCachedMediaUrl(fileId) {
  const cached = mediaUrlCache.get(fileId);
  if (!cached) {
    return '';
  }
  if (cached.expiresAt <= Date.now()) {
    mediaUrlCache.delete(fileId);
    return '';
  }
  return cached.url;
}

function cacheMediaUrl(fileId, url, expiresIn) {
  const expiresInSeconds = Number(expiresIn);
  if (!Number.isFinite(expiresInSeconds) || expiresInSeconds <= 0) {
    return;
  }
  const lifetimeMs = expiresInSeconds * 1000;
  const safetyMs = Math.min(
    MAX_CACHE_EXPIRY_SAFETY_MS,
    lifetimeMs * CACHE_EXPIRY_SAFETY_RATIO,
  );
  mediaUrlCache.set(fileId, {
    url,
    expiresAt: Date.now() + lifetimeMs - safetyMs,
  });
}

export default function InventoryImage({
  fileId,
  alt = '',
  height = 120,
  sx = {},
  imgSx = {},
  placeholderSx = {},
}) {
  const [state, setState] = useState({
    loading: Boolean(fileId),
    url: fileId ? getCachedMediaUrl(fileId) : '',
    error: '',
  });

  useEffect(() => {
    let isMounted = true;
    const normalizedFileId = String(fileId || '').trim();

    if (!normalizedFileId) {
      setState({ loading: false, url: '', error: '' });
      return () => {
        isMounted = false;
      };
    }

    const cachedUrl = getCachedMediaUrl(normalizedFileId);
    if (cachedUrl) {
      setState({ loading: false, url: cachedUrl, error: '' });
      return () => {
        isMounted = false;
      };
    }

    async function fetchImageUrl() {
      setState({ loading: true, url: '', error: '' });
      try {
        const response = await axiosInstance.post(
          `/capability-media/${encodeURIComponent(normalizedFileId)}/download`,
        );
        const url = response?.data?.url || '';
        if (url) {
          cacheMediaUrl(normalizedFileId, url, response?.data?.expires_in);
        }
        if (isMounted) {
          setState({ loading: false, url, error: url ? '' : 'No image URL returned.' });
        }
      } catch (error) {
        if (isMounted) {
          setState({
            loading: false,
            url: '',
            error: error?.response?.data?.message || error?.message || 'Image unavailable.',
          });
        }
      }
    }

    fetchImageUrl();
    return () => {
      isMounted = false;
    };
  }, [fileId]);

  const shellSx = {
    height,
    width: '100%',
    borderRadius: 1,
    overflow: 'hidden',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
    border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
    display: 'grid',
    placeItems: 'center',
    ...sx,
  };

  if (state.url) {
    return (
      <Box sx={shellSx}>
        <Box
          component="img"
          src={state.url}
          alt={alt}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            ...imgSx,
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...shellSx,
        color: state.error ? 'text.disabled' : 'primary.main',
        ...placeholderSx,
      }}
    >
      {state.loading ? <CircularProgress size={24} /> : <ImageRoundedIcon />}
    </Box>
  );
}
